// Pure helpers behind the landing-page insights. Money is integer ngwee throughout; the only
// non-integer arithmetic is in compactKwacha, which produces display text and nothing else.
import type { Budget, LineActual } from '@/data/budgets'
import type { Expense } from '@/data/expenses'
import type { ReconException } from '@/data/reconciliation'
import type { Remittance } from '@/data/remittances'
import type { FundBalance } from '@/data/income'
import { weekdayOf } from '@/lib/periods'

const hourFmt = new Intl.DateTimeFormat('en-GB', { timeZone: 'Africa/Lusaka', hour: '2-digit', hourCycle: 'h23' })

/** The hour of day (0-23) in Lusaka. */
export function lusakaHour(now: Date = new Date()): number {
  return Number(hourFmt.formatToParts(now).find((p) => p.type === 'hour')?.value ?? 12)
}

export function greetingFor(hour: number): string {
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/** '2026-10-07' -> 'Wednesday 7 October' (string parts only; never a Date built from the bare date). */
export function longDay(iso: string): string {
  const [, m, d] = iso.split('-').map(Number)
  return `${DAYS[weekdayOf(iso)]} ${d} ${MONTHS[m - 1]}`
}

/** '2026-Q4' -> 'Q4 2026' */
export function quarterLabel(id: string): string {
  const m = /^(\d{4})-(Q[1-4])$/.exec(id)
  return m ? `${m[2]} ${m[1]}` : id
}

/** Short Kwacha for chart axes: K850, K1.2k, K15k, K2.4m. Display only. */
export function compactKwacha(ngwee: number): string {
  const sign = ngwee < 0 ? '-' : ''
  const k = Math.abs(ngwee) / 100
  if (k < 1000) return `${sign}K${Math.round(k)}`
  if (k < 1_000_000) return `${sign}K${trim(k / 1000)}k`
  return `${sign}K${trim(k / 1_000_000)}m`
}
const trim = (n: number) => (n < 10 ? n.toFixed(1).replace(/\.0$/, '') : String(Math.round(n)))

/** Money the department has declared as sent that central treasury has not yet confirmed. */
export function unconfirmedNgwee(rows: Remittance[]): number {
  return rows
    .filter((r) => r.status === 'declared' || r.status === 'partially_settled' || r.status === 'disputed')
    .reduce((s, r) => s + Math.max(0, r.declaredNgwee - r.matchedNgwee), 0)
}

export type UseTone = 'normal' | 'warning' | 'danger'

export interface BudgetUse {
  budgetedNgwee: number
  spentNgwee: number
  /** Sent or approved but not yet paid. */
  committedNgwee: number
  /** Spent + committed as a share of the budget, 0-100+ (not capped, so 120 means 20% over). */
  usedPercent: number
  tone: UseTone
  overLineIds: string[]
}

/** How much of an approved budget is used: amber from 90%, red once over. */
export function budgetUse(lines: LineActual[]): BudgetUse {
  const budgetedNgwee = lines.reduce((s, l) => s + l.budgetedNgwee, 0)
  const spentNgwee = lines.reduce((s, l) => s + l.actualNgwee, 0)
  const committedNgwee = lines.reduce((s, l) => s + l.committedNgwee, 0)
  const used = spentNgwee + committedNgwee
  const usedPercent = budgetedNgwee > 0 ? Math.round((used * 100) / budgetedNgwee) : used > 0 ? 100 : 0
  const tone: UseTone = used > budgetedNgwee ? 'danger' : usedPercent >= 90 ? 'warning' : 'normal'
  return { budgetedNgwee, spentNgwee, committedNgwee, usedPercent, tone, overLineIds: lines.filter((l) => l.remainingNgwee < 0).map((l) => l.lineId) }
}

export type TodoTarget = 'budgets' | 'expenses' | 'remittances'

export interface TodoItem {
  key: string
  label: string
  count: number
  target: TodoTarget
  /** The latest reason given, for items sent back. */
  reason?: string
  urgent: boolean
}

/** What is waiting on the department, most pressing first. Items with nothing waiting are left out. */
export function departmentTodos(budgets: Budget[], expenses: Expense[], exceptions: ReconException[]): TodoItem[] {
  const sentBackBudgets = budgets.filter((b) => b.status === 'rejected')
  const sentBackExpenses = expenses.filter((e) => e.status === 'rejected')
  const items: TodoItem[] = [
    { key: 'queries', label: 'Queries from central treasury', count: exceptions.length, target: 'remittances', urgent: true },
    { key: 'budgets-back', label: 'Budgets sent back', count: sentBackBudgets.length, target: 'budgets', reason: sentBackBudgets[0]?.decisionNote ?? undefined, urgent: true },
    { key: 'expenses-back', label: 'Expenses sent back', count: sentBackExpenses.length, target: 'expenses', reason: sentBackExpenses[0]?.decisionNote ?? undefined, urgent: true },
    { key: 'unpaid', label: 'Approved expenses not yet marked paid', count: expenses.filter((e) => e.status === 'approved').length, target: 'expenses', urgent: false },
    { key: 'budget-drafts', label: 'Budget drafts not sent', count: budgets.filter((b) => b.status === 'draft').length, target: 'budgets', urgent: false },
    { key: 'expense-drafts', label: 'Expense drafts not sent', count: expenses.filter((e) => e.status === 'draft').length, target: 'expenses', urgent: false },
  ]
  return items.filter((i) => i.count > 0)
}

/** Passed-on money (tithe and the like) counted but not yet sent on. */
export function heldNotSent(funds: FundBalance[]): number {
  return funds.filter((f) => f.destination === 'passed_on').reduce((s, f) => s + Math.max(0, f.heldNgwee), 0)
}

/** "just now", "5 min ago", "2 h ago", "yesterday", "3 days ago", then the plain date. Display only. */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return iso
  const mins = Math.floor((now.getTime() - then) / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} h ago`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days} days ago`
  return new Date(then).toLocaleDateString('en-GB', { timeZone: 'Africa/Lusaka', day: 'numeric', month: 'short' })
}
